import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, map, mergeMap, switchMap} from 'rxjs/operators';
import {concat, EMPTY, of, throwError} from 'rxjs';

import { OrderService } from 'libs/order/src/lib/services';

import { TreatmentService } from '../services/treatment.lib.service';
import {
	AssignAndGoToTreatment,
	CreateWebviewerModification,
	CreateWebviewerModificationSuccess,
	InitiateCaseAssessmentOrder,
	InitiateSubsequentOrder,
	OrderNextPackage,
	RejectCaseAssessmentRequest,
	RejectRefinementRequest,
	ReleaseRefinementRequest,
	RequestRefinement,
	RequestRefinementRelease,
	SubmitCaseAssessment,
	SubmitComplaint,
	SubmitComplaintSuccess,
	SubmitReleaseRequest,
	SubmitReleaseRequestFunctionAL,
	TreatmentActionTypes,
	TreatmentRequested,
	UpdateWebviewerModification,
	UpdateWebviewerModificationSuccess,
	UpsertTreatment,
	UpsertTreatmentOnDb
} from './treatment.actions';
import { MessagesRequested, UpsertMessage } from 'libs/messages/src/lib/store/messages.actions';
import { SpecialCaseWorker, TreatmentModel, TreatmentStatus } from '../models/treatment.model';
import { GoAction } from 'libs/tm-common/src/lib/store/actions/actions';
import { NotificationService } from 'libs/tm-common/src/lib/services/notification.lib.service';
import { CaseAssessmentTreatmentSelectedAction, ParentTreatmentSelectedAction, PatientSelectedAction, ProductSelectedAction, SetDiscountedMemotainQuotaAction } from 'libs/anamnese/src/lib/store/anamnese.actions';
import { NavigationExtras } from '@angular/router';
import { WebviewerModificationActions } from 'libs/web-viewer/src/lib/store/webviewer-modification.store';

@Injectable({
	providedIn: 'root'
})
export class TreatmentEffects {
	private actions$ = inject(Actions);
	private orderService = inject(OrderService);
	private treatmentService = inject(TreatmentService);
	private readonly notificationService = inject(NotificationService);


	treatment$ = createEffect(() => this.actions$.pipe(
		ofType<TreatmentRequested>(TreatmentActionTypes.TreatmentRequested),
		mergeMap(({ payload }) => this.treatmentService.getTreatment(payload.id)),
		map((treatment: TreatmentModel) => new UpsertTreatment({ treatment })),
		catchError(err => {
			console.error('error loading treatment');
			return throwError(() => new Error(err));
		})
	));

	treatmentUpdateDb$ = createEffect(() => this.actions$.pipe(
		ofType<UpsertTreatmentOnDb>(TreatmentActionTypes.UpsertTreatmentOnDb),
		mergeMap(( {payload} ) => this.treatmentService.updateTreatment(payload.treatmentId, payload.diff)),
		map((treatment: TreatmentModel) => new UpsertTreatment({treatment})),
		catchError(err => {
		 	console.error('error while updating treatment');
		 	return throwError( () => new Error(err));
		})
	));

	orderNextPackage$ = createEffect(() => this.actions$.pipe(
		ofType<OrderNextPackage>(TreatmentActionTypes.OrderNextPackage),
		map( action => action.payload),
		switchMap( ({treatmentId, packageType, interimScanOption}) => this.orderService.alignerFollowupOrder(treatmentId, packageType, interimScanOption)),
		map( treatment => new UpsertTreatment({treatment})),
		catchError(err => {
			console.error('error while updating treatment');
			return throwError( () => new Error(err));
		})
	));

	requestRefinement$ = createEffect(() => this.actions$.pipe(
		ofType<RequestRefinement>(TreatmentActionTypes.RequestRefinement),
		map( action => action.payload),
		switchMap( ({treatmentId, comment}) => this.orderService.requestRefinement(treatmentId, comment)),
		map( treatment => new UpsertTreatment({treatment})),
		catchError(err => {
			return throwError( () => new Error(err));
		})
	));

	requestRefinementRelease$ = createEffect(() => this.actions$.pipe(
		ofType<RequestRefinementRelease>(TreatmentActionTypes.RequestRefinementRelease),
		map( action => action.payload),
		switchMap( ({treatmentId, plannedAligners}) => this.orderService.requestRefinementRelease(treatmentId, plannedAligners)),
		switchMap((treatment: TreatmentModel) => [
			new UpsertTreatment({ treatment }),
			new MessagesRequested()
		]),
		catchError(err => {
			return throwError( () => new Error(err));
		})
	));

	releaseRefinementRequest$ = createEffect(() => this.actions$.pipe(
		ofType<ReleaseRefinementRequest>(TreatmentActionTypes.ReleaseRefinementRequest),
		map( action => action.payload),
		switchMap( ({treatmentId}) => this.orderService.releaseRefinementRequest(treatmentId)),
		map( treatment => new UpsertTreatment({treatment})),
		catchError(err => {
			return throwError( () => new Error(err));
		})
	));

	rejectRefinementRequest$ = createEffect(() => this.actions$.pipe(
		ofType<RejectRefinementRequest>(TreatmentActionTypes.RejectRefinementRequest),
		map( action => action.payload),
		switchMap( ({treatmentId}) => this.orderService.rejectRefinementRequest(treatmentId)),
		map( treatment => new UpsertTreatment({treatment})),
		catchError(err => {
			return throwError( () => new Error(err));
		})
	));

	initiateSubsequentOrder$ = createEffect(() => this.actions$.pipe(
		ofType<InitiateSubsequentOrder>(TreatmentActionTypes.InitiateSubsequentOrder),
		switchMap(({ payload }) => {
			const extras: NavigationExtras = {
				info: {isProfessionalSubsequentOrder: true}
			}
			return concat(
				of(new PatientSelectedAction(payload.patientId)),
				of(new ProductSelectedAction({key:payload.productKey})),
				of(new SetDiscountedMemotainQuotaAction(payload.quota)),
				of(new ParentTreatmentSelectedAction({ parentTreatmentId: payload.treatmentId })),
				of(new GoAction({ path: [`/anamnese`], extras: extras })),
			);
		}),
		catchError(err => {
			console.error('error while initiating subsequent ordering process');
			return throwError( () => new Error(err));
		})
	));

	initiateCaseAssessmentOrder$ = createEffect(() => this.actions$.pipe(
		ofType<InitiateCaseAssessmentOrder>(TreatmentActionTypes.InitiateCaseAssessmentOrder),
		switchMap(({ payload }) => {
			const extras: NavigationExtras = {
				info: {isCaseAssessmentSubsequentOrder: true}
			}
			return concat(
				of(new PatientSelectedAction(payload.caseAssessmentTreatment.patient._id)),
				of(new ProductSelectedAction({key: payload.caseAssessmentTreatment.caseAssessmentData.recommendedKey})),
				of(new CaseAssessmentTreatmentSelectedAction({ caseAssessmentTreatment: payload.caseAssessmentTreatment })),
				of(new GoAction({ path: [`/anamnese`], extras: extras })),
			);
		}),
		catchError(err => {
			console.error('error while initiating case assessment ordering process');
			return throwError( () => new Error(err));
		})
	));

	assignAndGoToTreatment$ = createEffect(() => this.actions$.pipe(
		ofType<AssignAndGoToTreatment>(TreatmentActionTypes.AssignAndGoToTreatment),
		switchMap(({ payload }) => {
			return concat(
				of(new UpsertTreatmentOnDb({ treatmentId: payload.treatment._id, diff: { caseworker: payload.username } })),
				of(new GoAction({ path: [`/treatment/edit/${payload.treatment._id}`] })),
			);
		}),
		catchError(err => {
			console.error('error while updating treatment');
			return throwError( () => new Error(err));
		})
	));

	submitComplaint$ = createEffect(() => this.actions$.pipe(
		ofType<SubmitComplaint>(TreatmentActionTypes.SubmitComplaint),
		mergeMap(({ payload }) => this.treatmentService.updateTreatment(payload.treatment._id, { status: TreatmentStatus.ComplaintReceived, caseworker: SpecialCaseWorker.REKLAMATION })),
		switchMap((treatment: TreatmentModel) => [
			new UpsertTreatment({ treatment }),
			new SubmitComplaintSuccess({ treatment })
		]),

		catchError(err => {
			console.error('error while updating treatment');
			return throwError(() => new Error(err));
		})
	));


	submitReleaseRequest$ = createEffect(() => this.actions$.pipe(
		ofType<SubmitReleaseRequest>(TreatmentActionTypes.SubmitReleaseRequest),
		mergeMap(({ payload }) =>
			this.treatmentService.submitReleaseRequest(payload.treatmentId, payload.releaseRequest )
		),
		switchMap(({ treatment, message, modification, modificationAlternativeOption }) => [
			...(message ? [new UpsertMessage({ message })] : []), // Message object can be missing, when no comment was included.
			new UpsertTreatment({ treatment }),
			...(modification
				? [WebviewerModificationActions.upsertDocument({ payload: { document: modification } })]
				: []),
			...(modificationAlternativeOption
				? [WebviewerModificationActions.upsertDocument({ payload: { document: modificationAlternativeOption } })]
				: []),
		]),
		catchError(err => {
			this.notificationService.showErrorMessage(
				'ADMIN.requestRelease-error',
				(err.status && err.statusText) ? `${err.status} ${err.statusText}: ${err.error?.message ?? err.message}` : err.message
			);
			return throwError(() => new Error(err));
		})
	));

	submitReleaseRequestFunctionAL$ = createEffect(() => this.actions$.pipe(
		ofType<SubmitReleaseRequestFunctionAL>(TreatmentActionTypes.SubmitReleaseRequestFunctionAL),
		mergeMap(({ payload }) =>
			this.treatmentService.submitReleaseRequestFunctionAL(payload.treatmentId, payload.language)
		),
		switchMap(({ treatment, message, modification }) => [
			...(message ? [new UpsertMessage({ message })] : []), // Message object can be missing, when no comment was included.
			new UpsertTreatment({ treatment }),
			...(modification
				? [WebviewerModificationActions.upsertDocument({ payload: { document: modification } })]
				: []),
		]),
		catchError(err => {
			this.notificationService.showErrorMessage(
				'ADMIN.requestRelease-error',
				(err.status && err.statusText) ? `${err.status} ${err.statusText}: ${err.error?.message ?? err.message}` : err.message
			);
			return throwError(() => new Error(err));
		})
	));

	submitCaseAssessment$ = createEffect(() => this.actions$.pipe(
		ofType<SubmitCaseAssessment>(TreatmentActionTypes.SubmitCaseAssessment),
		mergeMap(({ payload }) => this.treatmentService.submitCaseAssessment(payload.treatmentId, payload.caseAssessmentData)),
		switchMap((treatment: TreatmentModel) => [
			new UpsertTreatment({ treatment }),
			new MessagesRequested()
		]),
		catchError(err => {
			this.notificationService.showErrorMessage(
				'submitCaseAssessmentError',
				(err.status && err.statusText) ? `${err.status} ${err.statusText}: ${err.error?.message ?? err.message}` : err.message
			);
			return throwError(() => new Error(err));
		})
	));

	rejectCaseAssessmentRequest$ = createEffect(() => this.actions$.pipe(
		ofType<RejectCaseAssessmentRequest>(TreatmentActionTypes.RejectCaseAssessmentRequest),
		mergeMap(({ payload }) => this.treatmentService.rejectCaseAssessmentRequest(payload.treatmentId)),
		switchMap((treatment: TreatmentModel) => [
			new UpsertTreatment({ treatment })
		]),
		catchError(err => {
			this.notificationService.showErrorMessage(
				'rejectCaseAssessmentRequestError',
				(err.status && err.statusText) ? `${err.status} ${err.statusText}: ${err.error?.message ?? err.message}` : err.message
			);
			return throwError(() => new Error(err));
		})
	));

	createWebviewerModification$ = createEffect(() =>
		this.actions$.pipe(
			ofType<CreateWebviewerModification>(TreatmentActionTypes.CreateWebviewerModification),
			mergeMap(({ payload }) =>
				this.treatmentService.createWebviewerModification(
					payload.treatmentId,
					payload.modification,
					payload.webviewerUrl,
					payload.language
				)
			),
			switchMap(({ treatment, message, modification }) => [
				WebviewerModificationActions.upsertDocument({
					payload: { document: modification },
				}),
				new UpsertTreatment({ treatment }),
				...(message ? [new UpsertMessage({ message })] : []),
				new CreateWebviewerModificationSuccess({ modification }),
			]),
			catchError((err) => {
				this.notificationService.showErrorMessage(
					"createWebviewerModificationError",
					err.status && err.statusText
						? `${err.status} ${err.statusText}: ${err.error?.message ?? err.message}`
						: err.message
				);
				return throwError(() => new Error(err));
			})
		)
	);

	createWebviewerModificationSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType<CreateWebviewerModificationSuccess>(
					TreatmentActionTypes.CreateWebviewerModificationSuccess
				),
				map(() => {
					this.notificationService.showSuccessMessage("WebViewerModificationSaved", 3000);
				})
			),
		{ dispatch: false }
	);

	updateWebviewerModification$ = createEffect(() =>
		this.actions$.pipe(
			ofType<UpdateWebviewerModification>(TreatmentActionTypes.UpdateWebviewerModification),
			mergeMap(({ payload }) =>
				this.treatmentService.updateWebviewerModification(
					payload.treatmentId,
					payload.modificationId,
					payload.modification,
					payload.language
				).pipe(
					switchMap(({ treatment, message, modification }) => [
						WebviewerModificationActions.upsertDocument({
							payload: { document: modification },
						}),
						new UpsertTreatment({ treatment }),
						...(message ? [new UpsertMessage({ message })] : []),
						new UpdateWebviewerModificationSuccess({ modification }),
					]),
					catchError((err) => {
						this.notificationService.showErrorMessage(
							"updateWebviewerModificationError",
							err.status && err.statusText
								? `${err.status} ${err.statusText}: ${err.error?.message ?? err.message}`
								: err.message
						);
						return EMPTY;
					})
				)
			),
		)
	);
}
